import { useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import {
  Alert, Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, Typography,
} from '@mui/material'
import {
  useRegionRuns, useRegionUploads, useRemoveIngest, useRemoveRun, type RegionRun, type RegionUpload,
} from '../../api/hooks'
import { Mono, Surface } from '../../components/primitives'
import { Segmented } from '../../components/Segmented'
import { bytes, formatDateTime, num, pct } from '../../lib/format'
import { tokens } from '../../theme'
import { PersonName } from '../../lib/people'

const row = (columns: string) => ({
  display: 'grid', gridTemplateColumns: columns, alignItems: 'center', gap: 4, px: 5, py: 2.5,
  borderBottom: `1px solid ${tokens.color.ink200}`, '&:last-of-type': { borderBottom: 0 },
})
const head = { color: 'text.disabled' } as const

const RUN_COLUMNS = '170px 1fr 90px 80px 80px 80px 90px 80px 130px'
const IMPORT_COLUMNS = '1fr 170px 1fr 90px 90px 120px 100px'

const PAGE = 25

/**
 * What has happened to a region: every run of the checks over it, and the
 * files its polygons came from — a tab each, since runs pile up and would
 * bury the imports. Each import can be taken back out whole; the latest run
 * is the one whose results the map and table show.
 */
export function RegionHistory({ regionId, onOpenLatest, onNotice }: {
  regionId: string
  onOpenLatest: () => void
  onNotice: (message: string) => void
}) {
  const [params, setParams] = useSearchParams()
  const tab = params.get('tab') === 'imports' ? 'imports' : 'runs'
  const [limit, setLimit] = useState(PAGE)
  const runs = useRegionRuns(regionId, limit)
  const uploads = useRegionUploads(regionId)
  const count = (n: number | undefined) => (n === undefined ? '' : ` · ${num(n)}`)

  return (
    <Box sx={{ flex: 1, overflowY: 'auto', px: 8, py: 6 }}>
      <Box sx={{ mb: 4, display: 'flex' }}>
        <Segmented
          ariaLabel="History"
          value={tab}
          onChange={(v) => setParams((p) => {
            if (v === 'imports') p.set('tab', v)
            else p.delete('tab')
            return p
          })}
          options={[
            { value: 'runs', label: `Runs${count(runs.data?.total)}` },
            { value: 'imports', label: `Imports${count(uploads.data?.uploads.length)}` },
          ]}
        />
      </Box>
      {tab === 'runs' ? (
        <Runs
          regionId={regionId}
          onNotice={onNotice}
          data={runs.data}
          isLoading={runs.isLoading}
          loadingMore={runs.isFetching && limit > (runs.data?.runs.length ?? 0)}
          onMore={() => setLimit((l) => l + PAGE)}
          onOpenLatest={onOpenLatest}
        />
      ) : (
        <Imports data={uploads.data} isLoading={uploads.isLoading} onNotice={onNotice} />
      )}
    </Box>
  )
}

function Runs({ regionId, onNotice, data, isLoading, loadingMore, onMore, onOpenLatest }: {
  regionId: string
  onNotice: (message: string) => void
  data: { runs: RegionRun[]; total: number } | undefined
  isLoading: boolean
  loadingMore: boolean
  onMore: () => void
  onOpenLatest: () => void
}) {
  const runs = data?.runs ?? []
  const remove = useRemoveRun(regionId)
  const [confirming, setConfirming] = useState<RegionRun | null>(null)
  const [error, setError] = useState<string | null>(null)
  // The region's polygons carry this one's verdicts, so it stays.
  const current = runs.find((r) => r.status === 'completed')?.id
  return (
    <Box>
      {error && <Alert severity="warning" sx={{ mb: 3 }} onClose={() => setError(null)}>{error}</Alert>}
      <Surface sx={{ p: 0 }}>
        <Box sx={row(RUN_COLUMNS)}>
          {['Started', 'By', 'Status', 'Plots', 'Passed', 'Flagged', 'Rejected', 'Issues', ''].map((h, i) => (
            <Typography key={h || i} variant="overline" sx={{ ...head, textAlign: i >= 3 && i <= 7 ? 'right' : 'left' }}>{h}</Typography>
          ))}
        </Box>
        {!isLoading && runs.length === 0 && (
          <Typography variant="body2" sx={{ color: 'text.secondary', px: 5, py: 4 }}>
            The checks have not been run over this region yet.
          </Typography>
        )}
        {runs.map((r) => (
          <RunRow
            key={r.id}
            run={r}
            current={r.id === current}
            onOpen={onOpenLatest}
            onRemove={r.id !== current && r.status !== 'queued' && r.status !== 'running' ? () => setConfirming(r) : undefined}
          />
        ))}
      </Surface>
      {data && data.total > runs.length && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 3, mt: 3 }}>
          <Button size="small" variant="outlined" disabled={loadingMore} onClick={onMore}>
            {loadingMore ? 'Loading…' : 'Show more'}
          </Button>
          <Typography variant="caption" sx={{ color: 'text.secondary' }}>
            Showing {num(runs.length)} of {num(data.total)}
          </Typography>
        </Box>
      )}

      <Dialog open={Boolean(confirming)} onClose={() => setConfirming(null)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ font: `700 18px ${tokens.font.display}` }}>Remove this run?</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            {confirming && (
              <>
                The run of {formatDateTime(confirming.created_at)} and the issues it found will be deleted.
                The polygons themselves are not touched. This cannot be undone.
              </>
            )}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button variant="outlined" onClick={() => setConfirming(null)}>Cancel</Button>
          <Button
            color="error"
            disabled={remove.isPending}
            onClick={() => {
              const target = confirming!
              remove.mutate(target.id, {
                onSuccess: () => {
                  setConfirming(null)
                  onNotice(`Removed the run of ${formatDateTime(target.created_at)}.`)
                },
                onError: (e) => {
                  setConfirming(null)
                  setError((e as Error).message)
                },
              })
            }}
          >
            {remove.isPending ? 'Removing…' : 'Remove'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}

function RunRow({ run, current, onOpen, onRemove }: {
  run: RegionRun
  /** The run whose verdicts the region's polygons carry. */
  current: boolean
  onOpen: () => void
  onRemove?: () => void
}) {
  const t = run.totals
  const took = run.started_at && run.finished_at
    ? Math.max(1, Math.round((Date.parse(run.finished_at) - Date.parse(run.started_at)) / 1000))
    : null
  const status =
    run.status === 'completed' ? (t ? `${pct(t.compliant_pct)} compliant` : 'Completed')
    : run.status === 'running' ? 'Running…'
    : run.status === 'queued' ? 'Queued'
    : run.status === 'failed' ? 'Failed'
    : 'Cancelled'
  const tone = run.status === 'failed' ? tokens.color.danger : run.status === 'completed' ? 'text.primary' : 'text.secondary'
  const cell = (n: number | null | undefined, colour?: string) => (
    <Typography variant="body2" sx={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums', color: n ? colour : 'text.disabled' }}>
      {n == null ? '—' : num(n)}
    </Typography>
  )
  return (
    <Box sx={row(RUN_COLUMNS)} title={run.error ?? undefined}>
      <Box>
        <Typography variant="body2">{formatDateTime(run.created_at)}</Typography>
        <Typography variant="caption" sx={{ color: 'text.secondary' }}>
          {num(run.checks)} checks{took ? ` · ${took < 120 ? `${took}s` : `${Math.round(took / 60)} min`}` : ''}
        </Typography>
      </Box>
      <Typography variant="body2" noWrap sx={{ color: run.created_by ? 'text.primary' : 'text.disabled' }}>
        <PersonName id={run.created_by_id} fallback={run.created_by ?? '—'} />
      </Typography>
      <Typography variant="body2" sx={{ color: tone, whiteSpace: 'nowrap' }}>{status}</Typography>
      {cell(t?.plots)}
      {cell(t?.passed)}
      {cell(t?.flagged, tokens.color.warning)}
      {cell(t?.rejected, tokens.color.danger)}
      {cell(run.issues)}
      <Box sx={{ textAlign: 'right' }}>
        {current ? (
          <Button size="small" variant="outlined" onClick={onOpen}>Open results</Button>
        ) : onRemove ? (
          <Button size="small" variant="outlined" color="error" onClick={onRemove}>Remove</Button>
        ) : null}
      </Box>
    </Box>
  )
}

function Imports({ data, isLoading, onNotice }: {
  data: { uploads: RegionUpload[]; drawn_plots: number } | undefined
  isLoading: boolean
  onNotice: (message: string) => void
}) {
  const remove = useRemoveIngest()
  const [confirming, setConfirming] = useState<RegionUpload | null>(null)
  const [error, setError] = useState<string | null>(null)
  const uploads = data?.uploads ?? []

  return (
    <Box>
      {error && <Alert severity="warning" sx={{ mb: 3 }} onClose={() => setError(null)}>{error}</Alert>}
      <Surface sx={{ p: 0 }}>
        <Box sx={row(IMPORT_COLUMNS)}>
          {['File', 'Imported', 'By', 'Features', 'Here', 'Moved away', ''].map((h, i) => (
            <Typography key={h || i} variant="overline" sx={{ ...head, textAlign: i >= 3 && i <= 5 ? 'right' : 'left' }}>{h}</Typography>
          ))}
        </Box>
        {!isLoading && uploads.length === 0 && (
          <Typography variant="body2" sx={{ color: 'text.secondary', px: 5, py: 4 }}>
            No files have been imported into this region.
          </Typography>
        )}
        {uploads.map((u) => (
          <Box key={u.id} sx={row(IMPORT_COLUMNS)}>
            <Box sx={{ minWidth: 0 }}>
              <Mono sx={{ display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={u.filename ?? undefined}>
                {u.filename ?? (u.source === 'drawn' ? 'Drawn on the map' : 'Untitled')}
              </Mono>
              {u.byte_size ? <Typography variant="caption" sx={{ color: 'text.secondary' }}>{bytes(u.byte_size)}</Typography> : null}
            </Box>
            <Typography variant="body2">{formatDateTime(u.created_at)}</Typography>
            <Typography variant="body2" noWrap sx={{ color: u.uploaded_by ? 'text.primary' : 'text.disabled' }}>
              <PersonName id={u.uploaded_by_id} fallback={u.uploaded_by ?? '—'} />
            </Typography>
            <Typography variant="body2" sx={{ textAlign: 'right' }}>{num(u.feature_count)}</Typography>
            <Typography variant="body2" sx={{ textAlign: 'right' }}>{num(u.plots_here)}</Typography>
            <Typography variant="body2" sx={{ textAlign: 'right', color: u.plots_elsewhere ? 'text.primary' : 'text.disabled' }}>
              {num(u.plots_elsewhere)}
            </Typography>
            <Box sx={{ textAlign: 'right' }}>
              <Button size="small" variant="outlined" color="error" onClick={() => setConfirming(u)}>Remove</Button>
            </Box>
          </Box>
        ))}
      </Surface>
      {(data?.drawn_plots ?? 0) > 0 && (
        <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary', mt: 2 }}>
          And {num(data!.drawn_plots)} {data!.drawn_plots === 1 ? 'polygon' : 'polygons'} drawn on the map.
        </Typography>
      )}

      <Dialog open={Boolean(confirming)} onClose={() => setConfirming(null)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ font: `700 18px ${tokens.font.display}` }}>Remove this import?</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            {confirming && (
              <>
                <Mono>{confirming.filename ?? 'This import'}</Mono> and its{' '}
                {num(confirming.plots_here + confirming.plots_elsewhere)} polygons will be deleted
                {confirming.plots_elsewhere > 0 ? `, including ${num(confirming.plots_elsewhere)} since moved to other regions` : ''}.
                If it is the region’s last upload, the region’s runs are removed with it. This cannot be undone.
              </>
            )}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button variant="outlined" onClick={() => setConfirming(null)}>Cancel</Button>
          <Button
            color="error"
            disabled={remove.isPending}
            onClick={() => {
              const target = confirming!
              remove.mutate(target.id, {
                onSuccess: (r) => {
                  setConfirming(null)
                  onNotice(`Removed ${target.filename ?? 'the import'}: ${num(r.plots_deleted)} polygons deleted.` +
                    (r.plots_kept ? ` ${num(r.plots_kept)} in regions you cannot open were left.` : ''))
                },
                onError: (e) => {
                  setConfirming(null)
                  setError((e as Error).message)
                },
              })
            }}
          >
            {remove.isPending ? 'Removing…' : 'Remove'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}
